import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

export interface Invitation {
  id: string;
  email: string;
  company_id: string;
  role_id: string;
  token: string;
  expires_at: string;
  accepted_at: string | null;
  created_by: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  created_at: string;
  updated_at: string;
}

interface InvitationRPCResult {
  id: string;
  email: string;
  company_id: string;
  role_id: string;
  token: string;
  expires_at: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  company_name: string;
  role_name: string;
  role_code: string;
}

export type InvitationStatus = 'accepted' | 'expired' | 'pending';

export const getInvitationStatus = (invitation: Invitation): InvitationStatus => {
  if (invitation.accepted_at) return 'accepted';
  if (new Date(invitation.expires_at) < new Date()) return 'expired';
  return 'pending';
};

export interface EmailAvailability {
  email_exists: boolean;
  company_name: string | null;
  role_code: string | null;
  pending_invitation: boolean;
  pending_company: string | null;
  pending_expires_at: string | null;
}

export const checkEmailAvailability = async (email: string): Promise<EmailAvailability> => {
  const { data, error } = await supabase
    .rpc('check_email_availability' as any, { _email: email })
    .maybeSingle();

  if (error) throw error;

  return (data as EmailAvailability) ?? {
    email_exists: false,
    company_name: null,
    role_code: null,
    pending_invitation: false,
    pending_company: null,
    pending_expires_at: null,
  };
};

export const useAllInvitations = () => {
  return useQuery({
    queryKey: ['invitations', 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invitations')
        .select('*, companies (name), roles (name, code)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as (Invitation & {
        companies: { name: string } | null;
        roles: { name: string; code: string } | null;
      })[];
    },
  });
};

export const useInvitations = () => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: invitations = [], isLoading } = useQuery({
    queryKey: ['invitations'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      // Récupérer le company_id de l'utilisateur
      const { data: userData } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!userData?.company_id) throw new Error('Company not found');

      const { data, error } = await supabase
        .from('invitations')
        .select('*')
        .eq('company_id', userData.company_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Invitation[];
    },
  });

  const createInvitation = useMutation({
    mutationFn: async (invitation: {
      email: string;
      role_id: string;
      first_name: string;
      last_name: string;
      phone: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userData } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!userData?.company_id) throw new Error('Company not found');

      const { data, error } = await supabase
        .from('invitations')
        .insert({
          ...invitation,
          company_id: userData.company_id,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as Invitation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });

  const sendInvitationEmail = async (invitation: Invitation, companyName: string, roleName: string) => {
    try {
      const frontendUrl = window.location.origin;
      const invitationUrl = `${frontendUrl}/invite/${invitation.token}`;
      
      const { error } = await supabase.functions.invoke('send-invitation-email', {
        body: {
          email: invitation.email,
          first_name: invitation.first_name,
          last_name: invitation.last_name,
          token: invitation.token,
          company_name: companyName,
          role_name: roleName,
          invitation_url: invitationUrl,
        },
      });

      if (error) throw error;

      toast({
        title: t('fleet.emailResent'),
        description: t('fleet.emailResentDescription', { email: invitation.email }),
      });
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      toast({
        title: t('common.error'),
        description: t('fleet.invitationSendError'),
        variant: "destructive",
      });
    }
  };

  const deleteInvitation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from('invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast({
        title: "Invitation annulée",
        description: "L'invitation a été supprimée avec succès",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de supprimer l'invitation",
        variant: "destructive",
      });
    },
  });

  return {
    invitations,
    isLoading,
    createInvitation,
    sendInvitationEmail,
    deleteInvitation,
  };
};

export const useInvitationByToken = (token: string) => {
  return useQuery({
    queryKey: ['invitation', token],
    queryFn: async () => {
      // Use secure RPC function - types will be regenerated after migration
      const { data, error } = await supabase
        .rpc('get_invitation_by_token' as any, { invitation_token: token })
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      // Cast to our local interface until Supabase types are regenerated
      const invitationData = data as InvitationRPCResult;

      // Transform RPC result to match expected format
      return {
        id: invitationData.id,
        email: invitationData.email,
        company_id: invitationData.company_id,
        role_id: invitationData.role_id,
        first_name: invitationData.first_name,
        last_name: invitationData.last_name,
        phone: invitationData.phone,
        token: invitationData.token,
        expires_at: invitationData.expires_at,
        companies: { name: invitationData.company_name },
        roles: { name: invitationData.role_name, code: invitationData.role_code }
      };
    },
    enabled: !!token,
  });
};
